import { createContext, useContext, useReducer, ReactNode } from 'react'
import {
  DreamityActionResDto,
  DreamityActionSaveReqDto,
} from '../types/calendar.types'
import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import { scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

interface ActionState {
  actions: DreamityActionResDto[]
  selectedAction: DreamityActionResDto | null
  loading: boolean
  error: string | null
  dateRange: {
    startDate: string | null
    endDate: string | null
  }
}

type ActionAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ACTIONS'; payload: DreamityActionResDto[] }
  | { type: 'SET_SELECTED_ACTION'; payload: DreamityActionResDto | null }
  | { type: 'UPDATE_ACTION_IN_LIST'; payload: DreamityActionResDto }
  | { type: 'DELETE_ACTION'; payload: string }
  | { type: 'SET_DATE_RANGE'; payload: { startDate: string; endDate: string } }

interface ActionContextType extends ActionState {
  fetchActions: (startDate: string, endDate: string) => Promise<void>
  fetchActionById: (dreamId: string, actionId: string) => Promise<DreamityActionResDto>
  createAction: (dreamId: string, planId: string, action: DreamityActionSaveReqDto) => Promise<void>
  updateAction: (
    dreamId: string,
    planId: string,
    actionId: string | null,
    action: DreamityActionSaveReqDto
  ) => Promise<void>
  deleteAction: (dreamId: string, actionId: string) => Promise<void>
  selectAction: (action: DreamityActionResDto | null) => void
}

const ActionContext = createContext<ActionContextType | undefined>(undefined)

const initialState: ActionState = {
  actions: [],
  selectedAction: null,
  loading: false,
  error: null,
  dateRange: {
    startDate: null,
    endDate: null,
  },
}

function actionReducer(state: ActionState, action: ActionAction): ActionState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_ACTIONS':
      return { ...state, actions: action.payload }
    case 'SET_SELECTED_ACTION':
      return { ...state, selectedAction: action.payload }
    case 'UPDATE_ACTION_IN_LIST':
      return {
        ...state,
        actions: state.actions.map((a) =>
          a.actionId === action.payload.actionId ? action.payload : a
        ),
        selectedAction:
          state.selectedAction?.actionId === action.payload.actionId
            ? action.payload
            : state.selectedAction,
      }
    case 'DELETE_ACTION':
      return {
        ...state,
        actions: state.actions.filter((a) => a.actionId !== action.payload),
        selectedAction:
          state.selectedAction?.actionId === action.payload ? null : state.selectedAction,
      }
    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: {
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
        },
      }
    default:
      return state
  }
}

interface ActionProviderProps {
  children: ReactNode
}

export function ActionProvider({ children }: ActionProviderProps) {
  const [state, dispatch] = useReducer(actionReducer, initialState)

  const fetchActions = async (startDate: string, endDate: string) => {
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const start = startDate.replace(/-/g, '')
      const end = endDate.replace(/-/g, '')
      const actions = dreamityPlannerRepository.listActionsInRange(start, end)
      dispatch({ type: 'SET_ACTIONS', payload: actions })
      dispatch({
        type: 'SET_DATE_RANGE',
        payload: { startDate, endDate },
      })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const fetchActionById = async (dreamId: string, actionId: string) => {
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const action = dreamityPlannerRepository.getAction(dreamId, actionId)
      if (!action) {
        throw new Error('액션을 찾을 수 없습니다.')
      }
      dispatch({ type: 'SET_SELECTED_ACTION', payload: action })
      dispatch({ type: 'UPDATE_ACTION_IN_LIST', payload: action })
      return action
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      throw error
    }
  }

  const createAction = async (dreamId: string, planId: string, action: DreamityActionSaveReqDto) => {
    if (!dreamId) {
      const err = new Error('dreamId is required to create action')
      dispatch({ type: 'SET_ERROR', payload: err.message })
      return
    }
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.createAction(dreamId, planId, action)
      if (state.dateRange.startDate && state.dateRange.endDate) {
        await fetchActions(state.dateRange.startDate, state.dateRange.endDate)
      }
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const updateAction = async (
    dreamId: string,
    planId: string,
    actionId: string | null,
    action: DreamityActionSaveReqDto
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.updateAction(dreamId, planId, actionId, action)
      if (state.dateRange.startDate && state.dateRange.endDate) {
        await fetchActions(state.dateRange.startDate, state.dateRange.endDate)
      }
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const deleteAction = async (dreamId: string, actionId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      dreamityPlannerRepository.deleteAction(dreamId, actionId)
      dispatch({ type: 'DELETE_ACTION', payload: actionId })
      if (state.dateRange.startDate && state.dateRange.endDate) {
        await fetchActions(state.dateRange.startDate, state.dateRange.endDate)
      }
      scheduleDebouncedPushDreamitySnapshot()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const selectAction = (action: DreamityActionResDto | null) => {
    dispatch({ type: 'SET_SELECTED_ACTION', payload: action })
  }

  return (
    <ActionContext.Provider
      value={{
        ...state,
        fetchActions,
        fetchActionById,
        createAction,
        updateAction,
        deleteAction,
        selectAction,
      }}
    >
      {children}
    </ActionContext.Provider>
  )
}

export function useActionContext() {
  const context = useContext(ActionContext)
  if (context === undefined) {
    throw new Error('useAction must be used within an ActionProvider')
  }
  return context
}
